import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

/**
 * Class names joined, with later Tailwind utilities winning over earlier ones
 * of the same kind. Sizes are `type-*` utilities, never `text-*`, so a size
 * is not mistaken for a colour here.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/** Up to two initials for an avatar: "Ada Lovelace" gives "AL". */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return "?"
  const first = Array.from(words[0])[0] ?? ""
  const last = words.length > 1 ? (Array.from(words[words.length - 1])[0] ?? "") : ""
  return (first + last).toUpperCase()
}

/**
 * Returns the path only if it is a same-origin app path, otherwise undefined.
 * Use for any redirect target read from the URL, so `?redirect=` can never
 * send someone to another site.
 */
export function safeRedirectPath(raw: string | undefined | null): string | undefined {
  if (!raw || !raw.startsWith("/") || raw.startsWith("//") || raw.includes("\\")) {
    return undefined
  }
  return raw
}

/** A value from localStorage, or null when there is none or storage is closed. */
export function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

/** Stores a value in localStorage; `null` removes it. Never throws. */
export function writeStored(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    // Private mode or a full disk: nothing is remembered, nothing breaks.
  }
}
